import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import logoSvg from '../../assets/logo.png';
import {
  RiDashboardLine,
  RiTeamLine,
  RiCalendarCheckLine,
  RiTaskLine,
  RiFileList3Line,
  RiLogoutBoxLine,
  RiMenuFoldLine,
  RiMenuUnfoldLine,
  RiUserLine,
  RiListCheck2,
  RiErrorWarningLine,
  RiTimeLine,
} from 'react-icons/ri';

const adminLinks = [
  { to: '/portal/admin',            label: 'Dashboard',      icon: RiDashboardLine, end: true },
  { to: '/portal/admin/users',      label: 'User Management',icon: RiTeamLine },
  { to: '/portal/admin/updates',    label: 'Daily Updates',  icon: RiFileList3Line },
  { to: '/portal/admin/tasks',      label: 'Task Overview',  icon: RiTaskLine },
  { to: '/portal/admin/todos',      label: 'Personal TODO',  icon: RiListCheck2 },
  { to: '/portal/admin/leaves',     label: 'Leave Requests', icon: RiCalendarCheckLine, notifyKey: 'pendingLeaves' },
  { to: '/portal/admin/attendance', label: 'Attendance',     icon: RiTimeLine },
];

const employeeLinks = [
  { to: '/portal/employee',            label: 'Dashboard',    icon: RiDashboardLine, end: true },
  { to: '/portal/employee/updates',    label: 'Daily Updates',icon: RiFileList3Line },
  { to: '/portal/employee/tasks',      label: 'My Tasks',     icon: RiTaskLine },
  { to: '/portal/employee/todos',      label: 'Personal TODO',icon: RiListCheck2 },
  { to: '/portal/employee/leave',      label: 'Leave Request',icon: RiCalendarCheckLine },
  { to: '/portal/employee/attendance', label: 'Attendance',   icon: RiTimeLine },
  { to: '/portal/employee/profile',    label: 'My Profile',   icon: RiUserLine },
];

export default function PortalSidebar({ role }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed,       setCollapsed]       = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [notify,          setNotify]          = useState({ pendingLeaves: 0 });

  const links = role === 'admin' ? adminLinks : employeeLinks;

  // Admin-only: poll pending leave requests so the Leave Requests nav item
  // shows a green dot whenever there are entries awaiting review.
  useEffect(() => {
    if (role !== 'admin') return undefined;

    let cancelled = false;
    let inFlight = false;
    let lastFetched = 0;
    const MIN_INTERVAL = 5 * 60_000; // throttle: at most one call every 5 min

    const fetchPending = (force = false) => {
      if (inFlight) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      const now = Date.now();
      if (!force && now - lastFetched < MIN_INTERVAL) return;
      inFlight = true;
      lastFetched = now;
      api.get('/leaves', { params: { status: 'pending', limit: 1 } })
        .then((res) => {
          if (cancelled) return;
          const d = res.data;
          const total = d?.meta?.total
            ?? (Array.isArray(d) ? d.length
              : Array.isArray(d?.leaves) ? d.leaves.length
              : Array.isArray(d?.data) ? d.data.length : 0);
          setNotify((n) => ({ ...n, pendingLeaves: total }));
        })
        .catch(() => { /* silent — don't break sidebar */ })
        .finally(() => { inFlight = false; });
    };

    fetchPending();
    const id = setInterval(fetchPending, MIN_INTERVAL);

    const onVisible = () => {
      if (!document.hidden) fetchPending();
    };
    const onLeavesChanged = () => fetchPending(true);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('leaves:changed', onLeavesChanged);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('leaves:changed', onLeavesChanged);
    };
  }, [role]);

  const confirmLogout = () => {
    logout();
    navigate('/portal/login');
  };

  const initials = (user?.name || user?.username || user?.email_id || 'U')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <aside
      className={`flex flex-col bg-white border-r border-[#E5E7EB] transition-all duration-300 ${
        collapsed ? 'w-[68px]' : 'w-60'
      } min-h-screen flex-shrink-0`}
    >
      {/* Logo */}
      <div className={`flex items-center border-b border-[#E5E7EB] ${collapsed ? 'justify-center px-4 py-4' : 'px-5 py-4 gap-3'}`}>
        <img src={logoSvg} alt="Logo" className="w-9 h-9 object-contain flex-shrink-0" />
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-[#1F2937] text-sm font-bold tracking-tight leading-tight">NEXCESS</p>
            <p className="text-[#6B7280] text-xs font-medium tracking-wide">INFOTECH</p>
          </div>
        )}
      </div>

      {/* Toggle + Role badge */}
      <div className={`flex items-center border-b border-[#E5E7EB] px-3 py-3 ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && (
          <span
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider ${
              role === 'admin'
                ? 'bg-[rgba(9,132,227,0.08)] text-[#0984e3] border border-[rgba(9,132,227,0.2)]'
                : 'bg-[rgba(0,176,255,0.08)] text-[#00b0ff] border border-[rgba(0,176,255,0.2)]'
            }`}
          >
            {role === 'admin' ? 'Administrator' : 'Employee'}
          </span>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-[#9CA3AF] hover:text-[#0984e3] transition-colors cursor-pointer p-1 rounded-lg hover:bg-[#F8FAFC]"
          aria-label="Toggle sidebar"
        >
          {collapsed ? <RiMenuUnfoldLine size={18} /> : <RiMenuFoldLine size={18} />}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2.5 py-3 space-y-0.5">
        {links.map(({ to, label, icon: Icon, end, notifyKey }) => {
          const notifyCount = notifyKey ? (notify[notifyKey] || 0) : 0;
          const showBadge = notifyCount > 0;
          const badgeText = notifyCount > 99 ? '99+' : String(notifyCount);
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer ${
                  isActive
                    ? 'text-white shadow-[0_4px_14px_rgba(9,132,227,0.3)]'
                    : 'text-[#6B7280] hover:bg-[#F8FAFC] hover:text-[#0984e3]'
                }`
              }
              style={({ isActive }) =>
                isActive
                  ? { background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 60%, #d500f9 100%)' }
                  : {}
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative flex-shrink-0">
                    <Icon size={18} />
                    {/* Collapsed mode → notification badge sits on the icon top-right */}
                    {collapsed && showBadge && (
                      <span
                        className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white shadow-[0_2px_6px_rgba(239,68,68,0.45)]"
                        title={`${notifyCount} pending`}
                      >
                        {badgeText}
                      </span>
                    )}
                  </span>
                  {!collapsed && (
                    <>
                      <span className="truncate flex-1">{label}</span>
                      {showBadge && (
                        <span className="relative flex items-center flex-shrink-0" title={`${notifyCount} pending`}>
                          {/* Pulsing ping ring */}
                          <span className="absolute inset-0 rounded-full bg-red-500 opacity-60 animate-ping" />
                          <span
                            className={`relative min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center shadow-[0_2px_8px_rgba(239,68,68,0.4)] ${
                              isActive
                                ? 'bg-white text-red-500'
                                : 'bg-red-500 text-white'
                            }`}
                          >
                            {badgeText}
                          </span>
                        </span>
                      )}
                    </>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User + Logout */}
      <div className="border-t border-[#E5E7EB] p-3 space-y-1">
        {!collapsed && (
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#F8FAFC] border border-[#E5E7EB] mb-1">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #0984e3 0%, #d500f9 100%)' }}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[#1F2937] text-xs font-semibold truncate">{user?.name || 'User'}</p>
              <p className="text-[#9CA3AF] text-xs truncate">{user?.username || user?.email_id || ''}</p>
            </div>
          </div>
        )}
        <button
          onClick={() => setShowLogoutModal(true)}
          className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm text-[#6B7280] hover:bg-red-50 hover:text-red-500 border border-transparent hover:border-red-100 transition-all duration-150 cursor-pointer ${collapsed ? 'justify-center' : ''}`}
        >
          <RiLogoutBoxLine size={17} className="flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>

      {/* ── Logout confirmation modal ──────────────────────────────────────── */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(15,23,42,0.18)] w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <RiErrorWarningLine size={20} className="text-red-500" />
              </div>
              <div>
                <h3 className="text-[#1F2937] font-bold text-base">Log out?</h3>
                <p className="text-[#6B7280] text-xs mt-0.5">You'll need to sign in again to access the portal.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-[#E5E7EB] text-[#374151] text-sm font-semibold hover:bg-[#F8FAFC] transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <RiLogoutBoxLine size={15} /> Log out
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
